import { Inject, Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import {
  AccountInfo,
  AuthenticationResult,
  InteractionRequiredAuthError,
} from '@azure/msal-browser';
import { OutlookStore } from '../../state';
import {
  catchError,
  finalize,
  from,
  lastValueFrom,
  Observable,
  of,
  switchMap,
  throwError,
} from 'rxjs';
import { GRAPH_API_SCOPES, GRAPH_API_URI } from '../../injection-token';
import { InsightsService } from '@maximizer/core/shared/insights';
import { normalizeError } from '@maximizer/shared/util';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { HttpClient } from '@angular/common/http';
import { GraphUserProfile, OutlookInsightsEvents } from '../../models';

@Injectable()
export class OutlookMsalService {
  private loginInProgress = false;
  initialized = false;

  constructor(
    @Inject(GRAPH_API_SCOPES) private readonly scopes: string[],
    @Inject(GRAPH_API_URI) private readonly graphURI: string,
    private readonly msalService: MsalService,
    private readonly insightsService: InsightsService,
    private readonly http: HttpClient,
    public outlookStore: OutlookStore,
  ) {}

  async initializeMsal(): Promise<void> {
    try {
      await this.msalService.instance.initialize();
      this.initialized = true;
    } catch (error) {
      this.trackException(
        'outlook-msal-initialization-failed',
        error instanceof Error
          ? error
          : new Error('Unknown error initializing MSAL'),
      );
      throw error;
    }
  }

  readGraphProfile(): Observable<GraphUserProfile> {
    const request = { scopes: this.getScopes() };

    return from(this.msalService.acquireTokenSilent(request)).pipe(
      switchMap((data) => {
        if (!data.accessToken) {
          return throwError(
            () => new Error('No token received to read graph profile.'),
          );
        }
        return this.http.get<GraphUserProfile>(this.graphURI + '/me', {
          headers: {
            Authorization: `Bearer ${data.accessToken}`,
          },
        });
      }),
      catchError((error) => {
        this.trackException('outlook-msal-graph-profile-failed', error);
        return throwError(() => error);
      }),
    );
  }

  private setGraphUserProperties(): void {
    this.readGraphProfile().subscribe((data) => {
      this.outlookStore.setGraphMailAccount(data.mail);
    });
  }

  private resolveActiveAccount(): AccountInfo | null {
    const activeAccount = this.msalService.instance.getActiveAccount();
    const accounts = this.msalService.instance.getAllAccounts();
    const account =
      activeAccount ?? (accounts.length > 0 ? accounts[0] : null);

    if (account) {
      this.msalService.instance.setActiveAccount(account);
    }

    return account;
  }

  checkAndSetActiveAccount() {
    if (this.resolveActiveAccount()) {
      this.setGraphUserProperties();
      this.outlookStore.setHasActiveAccount(true);
    }
  }

  loginPopup() {
    if (this.loginInProgress) return;

    this.loginInProgress = true;
    let request = { scopes: this.getScopes() };
    from(this.msalService.loginPopup(request))
      .pipe(
        switchMap((response) => {
          this.handleLoginResult(response);
          return of(response);
        }),
        catchError((error) => {
          if (error?.errorCode === 'user_cancelled') {
            this.trackTrace('outlook-msal-login-popup-cancelled');
          } else {
            this.trackException('outlook-msal-login-popup-failed', error);
          }
          return of(undefined);
        }),
        finalize(() => {
          this.loginInProgress = false;
        }),
      )
      .subscribe();
  }

  switchAccount() {
    if (this.loginInProgress) return;

    this.loginInProgress = true;

    from(
      this.msalService.loginPopup({
        scopes: this.scopes,
        prompt: 'select_account',
      }),
    )
      .pipe(
        switchMap((response) => {
          this.handleLoginResult(response);
          return of(response);
        }),
        catchError((error) => {
          if (error?.errorCode === 'user_cancelled') {
            this.trackTrace('outlook-msal-switch-account-cancelled');
          } else {
            this.trackException('outlook-msal-switch-account-failed', error);
          }
          return of(undefined);
        }),
        finalize(() => {
          this.loginInProgress = false;
        }),
      )
      .subscribe();
  }

  private handleLoginResult(response: AuthenticationResult | undefined): void {
    if (response?.account) {
      this.msalService.instance.setActiveAccount(response.account);
      this.outlookStore.setHasActiveAccount(true);
      this.setGraphUserProperties();
    } else {
      this.trackTrace(
        'outlook-msal-login-no-account',
        undefined,
        SeverityLevel.Warning,
      );
    }
  }

  async getMsalAuthentication(): Promise<AuthenticationResult> {
    if (!this.initialized) {
      await this.initializeMsal();
    }

    const account = this.resolveActiveAccount();
    if (!account) {
      throw new Error('MSAL: No active account available.');
    }

    const request = {
      scopes: this.getScopes(),
      account,
    };

    try {
      return await lastValueFrom(this.msalService.acquireTokenSilent(request));
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        return await lastValueFrom(this.msalService.acquireTokenPopup(request));
      }
      this.trackException('outlook-msal-silent-acquire-failed', error);
      throw error;
    }
  }

  private getScopes(): string[] {
    let request = { scopes: this.scopes };

    if (this.outlookStore.isSharedMailbox()) {
      request.scopes.push('Mail.ReadWrite.Shared');
    }

    return request.scopes;
  }

  private trackTrace(
    message: string,
    metadata?: Record<string, unknown>,
    severityLevel: SeverityLevel = SeverityLevel.Verbose,
  ): void {
    this.insightsService.trackTrace(
      { message, severityLevel },
      {
        component: 'OutlookMsalService',
        eventId: OutlookInsightsEvents.OutlookAuth,
        ...metadata,
      },
    );
  }

  private trackException(
    event: string,
    error: unknown,
    metadata?: Record<string, unknown>,
  ): void {
    const errorMessage = normalizeError(error);

    this.insightsService.trackException(
      { error: error instanceof Error ? error : new Error(errorMessage) },
      {
        component: 'OutlookMsalService',
        eventId: OutlookInsightsEvents.OutlookAuth,
        event,
        errorMessage,
        ...metadata,
      },
    );
  }
}
